import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { HelpCircle } from 'lucide-react';

const faqs = [
  {
    question: 'What is BMI and how is it calculated?',
    answer: 'BMI (Body Mass Index) is a measure of body fat based on height and weight. It\'s calculated by dividing your weight in kilograms by your height in meters squared (kg/m²). A BMI of 18.5-24.9 is generally considered healthy for adults.'
  },
  {
    question: 'What is Athlete Mode?',
    answer: 'Athlete Mode provides performance-focused interpretations of your BMI. Standard BMI categories don\'t account for higher muscle mass in athletes. Athlete Mode shows performance bands that help athletes understand how their body composition relates to endurance, power, and strength training goals.'
  },
  {
    question: 'What is the Waist-to-Height Ratio?',
    answer: 'Waist-to-Height Ratio is a simple yet powerful health indicator. Research shows it\'s often better than BMI at predicting health risks. A ratio under 0.5 indicates lower health risk. You can calculate it by adding your waist circumference measurement.'
  },
  {
    question: 'Is my data stored securely?',
    answer: 'Absolutely. All your data is stored locally on your device using browser storage. We never send your health information to any server. Your calculations, history, and personal metrics stay completely private on your own device.'
  },
  {
    question: 'How accurate is the Running Pace Impact Estimator?',
    answer: 'The Running Pace Impact Estimator uses research-based estimates that suggest approximately 1-2 seconds per kilometer change per kilogram of body weight. This is an approximation — individual results vary based on fitness level, running efficiency, and other factors.'
  },
  {
    question: 'Can I use this calculator if I\'m not an athlete?',
    answer: 'Yes! The standard BMI calculator works great for everyone. Athlete Mode is an optional feature for those interested in sport-specific interpretations. The WHO BMI categories and health metrics are useful for all adults regardless of athletic involvement.'
  },
  {
    question: 'Why might my BMI be misleading?',
    answer: 'BMI doesn\'t distinguish between muscle and fat mass. Athletes with significant muscle may have a higher BMI despite being very fit. That\'s why we include the Waist-to-Height Ratio and Athlete Mode — they provide additional context for a more complete picture of your health.'
  },
  {
    question: 'How often should I check my BMI?',
    answer: 'For general health monitoring, monthly or quarterly checks are sufficient. If you\'re actively working on weight management or training, weekly measurements can help track trends. Remember that daily weight fluctuations are normal and don\'t reflect actual changes in body composition.'
  },
];

export const FAQSection = () => {
  return (
    <section id="faq" className="py-16 sm:py-24 bg-muted/30">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <HelpCircle className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-muted-foreground">
            Everything you need to know about using BMI Calc Hub
          </p>
        </div>

        {/* FAQ Accordion */}
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((faq, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
              className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
            >
              <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline py-5">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pb-5">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Medical Disclaimer */}
        <div className="mt-12 p-6 rounded-2xl bg-warning-light border border-warning/20">
          <h3 className="font-semibold text-foreground mb-2">Medical Disclaimer</h3>
          <p className="text-sm text-muted-foreground">
            This calculator provides general health information and is not a substitute for professional medical advice, diagnosis, or treatment. Always consult with a qualified healthcare provider about your specific health needs and before making any changes to your diet or exercise routine.
          </p>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
